/********************************************************************************
 * Copyright (c) 2020 Cedalo GmbH
 *
 * This program and the accompanying materials are made available under the
 * terms of the Eclipse Public License 2.0 which is available at
 * http://www.eclipse.org/legal/epl-2.0.
 *
 * SPDX-License-Identifier: EPL-2.0
 *
 ********************************************************************************/
// eslint-disable-next-line
const GatewayClientWorker = require('./GatewayClientWorker');

// const logger = console;

const deletePendingRequest = (requestId, requests) => {
	const request = requests.get(requestId);
	if (request) {
		clearTimeout(request.timeoutId);
		requests.delete(requestId);
	}
	return request;
};
const timeoutHandler = (requestId, requests) => {
	const { reject } = deletePendingRequest(requestId, requests);
	reject({
		message: 'WebWorkerGatewayClientProxy: Timeout'
	});
};

// eslint-disable-next-line
module.exports = class WebWorkerGatewayClientProxy {
	constructor({ timeout = 500000 } = {}) {
		this._pendingRequests = new Map();
		this._eventListeners = new Map();
		this._workerInstance = new GatewayClientWorker();
		this.timeout = timeout;
		this._workerInstance.addEventListener('message', (event) => {
			this._handleMessage(event.data);
		});
	}

	on(event, listener) {
		let listeners = this._eventListeners.get(event);
		if (!listeners) {
			listeners = [];
			this._eventListeners.set(event, listeners);
		}
		listeners.push(listener);
	}

	off(event, listener) {
		const listeners = this._eventListeners.get(event);
		if (listeners) {
			const index = listeners.indexOf(listener);
			if (index > -1) {
				listeners.splice(index, 1);
			}
		}
	}

	connect(config) {
		// TODO: current config only necessary because of "restore" quick fix
		this._currentConfig = config;
		return this._proxy('connect', config);
	}

	waitUntilAllServersAreConnected(timeout) {
		return this._proxy('waitUntilAllServersAreConnected', timeout);
	}

	executeStreamCommand(scope, cmd) {
		return this._proxy('executeStreamCommand', scope, cmd);
	}

	graphql(query, variables, file) {
		return this._proxy('graphql', query, variables, file);
	}

	importMachineDefinition(importData, importAsNew) {
		return this._proxy('importMachineDefinition', importData, importAsNew);
	}

	backup() {
		return this._proxy('backup');
	}

	async restore(file) {
		// TODO: this results in a Chrome RESULT_CODE_KILLED_BAD_MESSAGE error
		// return this._proxy('restore', file);

		// Quick fix:
		// eslint-disable-next-line
		const formData = new FormData();
		formData.append('restoreData', file);
		const config = {
			headers: {
				"Accept": "application/json",
				"Authorization": `JWT ${this._currentConfig.token}`
			},
			method: 'POST',
			body: formData
		}
		// eslint-disable-next-line
		const response = await fetch(
			`${this._currentConfig.restEndpointURL}/restore`,
			config
		);
		const data = await response.json();
		return data;
	}

	cloneMachine(machineId, newNameSuffix = 'Copy') {
		return this._proxy('cloneMachine', machineId, newNameSuffix);
	}

	/**
	 * *********************************************
	 * User Definition API
	 * *********************************************
	 */

	// TODO: deprecate when done
	login(credentials) {
		return this._proxy('login', credentials);
	}

	authenticate(authRequest) {
		return this._proxy('authenticate', authRequest);
	}

	logout(id) {
		return this._proxy('logout', id);
	}

	/**
	 * *********************************************
	 * Machine API
	 * *********************************************
	 */
	getMachines() {
		return this._proxy('getMachines');
	}

	deleteMachine(machineId, scope) {
		return this._proxy('deleteMachine', machineId, scope);
	}

	deleteMachines() {
		return this._proxy('deleteMachines');
	}

	startAllMachines(singleMachineStartedCallback) {
		return this._proxy('startAllMachines', singleMachineStartedCallback);
	}

	stopAllMachines(singleMachineStoppedCallback) {
		return this._proxy('stopAllMachines', singleMachineStoppedCallback);
	}

	pauseAllMachines(singleMachinePausedCallback) {
		return this._proxy('pauseAllMachines', singleMachinePausedCallback);
	}

	deleteAllMachines(singleMachineDeletedCallback) {
		return this._proxy('deleteAllMachines', singleMachineDeletedCallback);
	}

	loadMachine(machineId, settings, scope) {
		return this._proxy('loadMachine', machineId, settings, scope);
	}

	unloadMachine(machineId, settings) {
		return this._proxy('unloadMachine', machineId, settings);
	}

	loadSubscribeMachine(machineId, settings, scope) {
		return this._proxy('loadSubscribeMachine', machineId, settings, scope);
	}

	openMachine(machineId) {
		return this._proxy('openMachine', machineId);
	}

	pauseMachine(machineId) {
		return this._proxy('pauseMachine', machineId);
	}

	updateMachineImage(machineId, previewImage) {
		return this._proxy('updateMachineImage', machineId, previewImage);
	}

	updateMachineTitleImage(machineId, titleImage) {
		return this._proxy('updateMachineTitleImage', machineId, titleImage);
	}

	renameMachine(machineId, newName) {
		return this._proxy('renameMachine', machineId, newName);
	}

	createStreamSheet(machineId, activeItemId, settings, scope) {
		return this._proxy(
			'createStreamSheet',
			machineId,
			activeItemId,
			settings,
			scope
		);
	}

	deleteStreamSheet(machineId, streamsheetId, scope) {
		return this._proxy('deleteStreamSheet', machineId, streamsheetId, scope);
	}

	updateStreamSheetStreams(machineId, streamsheetId, streams) {
		return this._proxy(
			'updateStreamSheetStreams',
			machineId,
			streamsheetId,
			streams
		);
	}

	confirmLoadSubscribeMachine(machineId) {
		return this._proxy('confirmLoadSubscribeMachine', machineId);
	}

	confirmProcessedMachineStep(machineId) {
		return this._proxy('confirmProcessedMachineStep', machineId);
	}

	startMachine(machineId) {
		return this._proxy('startMachine', machineId);
	}

	stepMachine(machineId) {
		return this._proxy('stepMachine', machineId);
	}

	stopMachine(machineId) {
		return this._proxy('stopMachine', machineId);
	}

	startMachines() {
		return this._proxy('startMachines');
	}

	stopMachines() {
		return this._proxy('stopMachines');
	}

	subscribeMachine(machineId) {
		return this._proxy('subscribeMachine', machineId);
	}

	unsubscribeMachine(machineId) {
		return this._proxy('unsubscribeMachine', machineId);
	}

	saveMachineAs(originalMachineId, newName) {
		return this._proxy('saveMachineAs', originalMachineId, newName);
	}

	saveMachineCopy(originalMachineId, newName) {
		return this._proxy('saveMachineCopy', originalMachineId, newName);
	}

	redo(machineId) {
		return this._proxy('redo', machineId);
	}

	undo(machineId) {
		return this._proxy('undo', machineId);
	}

	updateMachineSettings(machineId, settings) {
		return this._proxy('updateMachineSettings', machineId, settings);
	}

	getCellRawValue(machineId, streamsheetId, reference) {
		return this._proxy('getCellRawValue', machineId, streamsheetId, reference);
	}

	/**
	 * *********************************************
	 * General API
	 * *********************************************
	 */

	getMetaInformation(scope) {
		return this._proxy('getMetaInformation', scope);
	}

	/**
	 * *********************************************
	 * Graph API
	 * *********************************************
	 */
	sendCommand(machineId, command) {
		return this._proxy('sendCommand', machineId, command);
	}

	sendMachineAction(machineId, action) {
		return this._proxy('sendMachineAction', machineId, action);
	}

	/**
	 * *********************************************
	 * Administration API
	 * *********************************************
	 */

	saveDSConfiguration(scope, configuration) {
		return this._proxy('saveDSConfiguration', scope, configuration);
	}

	loadAllDSConfigurations(scope) {
		return this._proxy('loadAllDSConfigurations', scope);
	}

	deleteDSConfiguration(scope, configId) {
		return this._proxy('deleteDSConfiguration', scope, configId);
	}

	reloadStreams(scope, sources) {
		return this._proxy('reloadStreams', scope, sources);
	}

	/**
	 * ******************************************************************************************
	 * Low Level Proxy API
	 * ******************************************************************************************
	 */

	_proxy(method, ...args) {
		return this._send(
			{
				method,
				args: args || []
			},
			this._generateRequestId()
		);
	}

	_generateRequestId() {
		return Math.random();
	}

	_send(message, requestId) {
		// logger.info(`Sending message with id '${requestId}'`);
		return new Promise((resolve, reject) => {
			if (requestId) {
				message.requestId = requestId;
				this._pendingRequests.set(requestId, { resolve, reject });
				const timeoutId = setTimeout(
					() => timeoutHandler(requestId, this._pendingRequests),
					this.timeout
				);
				this._pendingRequests.set(requestId, {
					resolve,
					reject,
					timeoutId
				});
			} else {
				resolve();
			}
			// logger.info(`Send message for ${message.method}`);
			this._workerInstance.postMessage(message);
		});
	}

	_handleMessage(message) {
		// logger.info(`handle message: ${message.event ? message.event.type : message.type}`);

		const type = message.type;
		const request = deletePendingRequest(message.requestId, this._pendingRequests);

		if (request && type === 'response') {
			request.resolve(message.result);
		} else if (type === 'event') {
			this._handleEvent(message.event);
		} else if (request && type === 'error') {
			request.reject(message.error);
			// TODO: improve error handling!!
		}
	}

	_handleEvent(event) {
		const listeners = this._eventListeners.get(event.type) || [];
		listeners.forEach((listener) => listener(event));
	}
};
